import React, { useState, useEffect } from 'react';
import styles from './SchedulingFlow.module.css';
import { checkPatientStatus } from '../../utils/apis';
import { AxisFailurePopup } from './AxisFailurePopup';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
import AxisLogo from './SVGs/AxisLogo';


interface PatientFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string; // MM/DD/YYYY as entered by user
  phone: string;
  email: string;
}

interface PatientStatusStepProps {
  onBack: () => void;
  onProceed: (patientStatus: 'existing' | 'new', patientData: any) => void;
  isSpravatoFlow?: boolean;
}

const PatientStatusStep: React.FC<PatientStatusStepProps> = ({ onBack, onProceed, isSpravatoFlow = false }) => {
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 700);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const NAV_PHONE = '(720) 400-7025';
  const NAV_LINKS = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'home') {
      // Refresh the page to go back to landing
      window.location.reload();
    }
  };

  const handleInputChange = (field: keyof PatientFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Auto-format DOB as MM/DD/YYYY while typing
  const formatDobInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    let out = mm;
    if (dd) out += `/${dd}`;
    if (yyyy) out += `/${yyyy}`;
    return out;
  };

  const handleDobChange = (value: string) => {
    setFormData(prev => ({ ...prev, dateOfBirth: formatDobInput(value) }));
    setError(null);
  };

  const isValidMmDdYyyy = (value: string) => /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(value.trim());

  const normalizeDobToIso = (value: string): string => {
    const v = value.trim();
    if (v.includes('/')) {
      const [m, d, y] = v.split('/');
      const mm = m.padStart(2, '0');
      const dd = d.padStart(2, '0');
      return `${y}-${mm}-${dd}`;
    }
    return v; // assume already ISO
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) { setError('First name is required'); return false; }
    if (!formData.lastName.trim()) { setError('Last name is required'); return false; }
    if (!formData.dateOfBirth) { setError('Date of birth is required'); return false; }
    if (formData.dateOfBirth && formData.dateOfBirth.includes('/') && !isValidMmDdYyyy(formData.dateOfBirth)) {
      setError('Enter date of birth in MM/DD/YYYY');
      return false;
    }
    
    // Phone number is required
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    
    // Validate phone format (should be 10 digits minimum)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    
    // Email is optional, but validate format if provided
    if (formData.email.trim() && !formData.email.includes('@')) { 
      setError('Please enter a valid email address'); 
      return false; 
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError(null);
    try {
      const apiData: any = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        // normalize MM/DD/YYYY to ISO for backend
        dateOfBirth: normalizeDobToIso(formData.dateOfBirth),
      };
      if (formData.phone.trim()) apiData.phone = formData.phone.trim();
      if (formData.email.trim()) apiData.email = formData.email.trim();

      const response = await checkPatientStatus(apiData);
      if (isSpravatoFlow && (!response?.athenaPatientId || response?.existingPatient === false)) {
        setError('Spravato appointments are available to existing Axis patients. Please call (720) 400-7025 to schedule.');
        setLoading(false);
        return;
      }
      // Set status according to the rules:
      // 1. existingPatient is false -> status will be 'new'
      // 2. existingPatient is true -> status will be what comes from backend API response as status
      const status: 'existing' | 'new' = response?.existingPatient === false ? 'new' : (response?.status || 'new');
      const patientDataToPass = {
        ...apiData,
        existingPatient: response?.existingPatient || false,
        athenaPatientId: response?.athenaPatientId || null,
        upcomingAppointments: response?.upcomingAppointments || false,
      };
      onProceed(status, patientDataToPass);
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(err, 'verifying patient details');
      setError(userFriendlyError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.landingRoot}>
      {/* Top Banner with Background Image and Overlay */}
      <div className={styles.topBanner}>
        <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
        <div className={styles.bannerOverlay} />
        {/* Nav Bar */}
        <div className={styles.navBar}>
          <AxisLogo variant="white" size="extra-large" className={styles.navLogo} onClick={() => window.open('https://www.axismh.com/', '_blank')} style={{ cursor: 'pointer' }} />
          <span className={styles.navPhone}>{NAV_PHONE}</span>
          <div className={styles.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
            ))}
            {isSpravatoFlow && (
              <a
                href="https://go.axismh.com/spravato-prep"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.navLink}
                title="Spravato Preparation Tips & Help"
              >
                Help
              </a>
            )}
          </div>
        </div>
        <div className={styles.bannerContent}>
          <h1 className={styles.bannerTitle}>Book Online</h1>
          <div className={styles.bannerSubtitle}>
            Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className={styles.landingMain}>
        <div className={styles.landingHeader}>
          <h2 className={styles.landingTitle}>Tell us about yourself</h2>
          <div className={styles.landingSubtitle}>We'll use this to verify your record. Phone number is required.</div>
        </div>

        <div className={styles.patientStatusForm}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>First name *</label>
              <input
                className={styles.formInput}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="John"
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Last name *</label>
              <input
                className={styles.formInput}
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Doe"
              />
            </div>
          </div>

          {/* DOB in left column to match label alignment */}
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Date of Birth (MM/DD/YYYY) *</label>
              <input
                className={styles.formInput}
                value={formData.dateOfBirth}
                onChange={(e) => handleDobChange(e.target.value)}
                placeholder="02/14/1990"
                inputMode="numeric"
              />
            </div>
            <div className={styles.formField}>
              {/* spacer to keep two-column alignment; leave empty intentionally */}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Phone *</label>
              <input
                className={styles.formInput}
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="4155551234"
              />
              <div className={styles.phoneFormatSuggestion}>
                Phone numbers must follow the North American Numbering Plan. Should be of format: NPANXXXXXX.
              </div>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Email (optional)</label>
              <input
                className={styles.formInput}
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="john.doe@example.com"
              />
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage}>{error}</div>
          )}

          <div className={styles.formActions}>
            <button className={styles.backButton} onClick={onBack} disabled={loading}>← Back</button>
            <button className={styles.submitButton} onClick={handleSubmit} disabled={loading}>
              {loading ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.landingFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
          <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
          <div className={styles.footerRating}>
            <span className={styles.footerStars}>★★★★★</span>
            <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>

      {/* Friendly error modal */}
      <AxisFailurePopup
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
        title="We couldn't verify your details"
      />
    </div>
  );
};

export default PatientStatusStep;
